import { Link } from '@chakra-ui/react'
import styled from '@emotion/styled'
import { motion } from 'framer-motion'
import Image from 'next/image'
import type { ReactNode, VFC } from 'react'

import CommonMeta from '../head/commonMeta'

export type DefaultLayoutProps = {
    children: ReactNode
    pageTitle: string
}

const spWidth = 599
const pcWidth = 1024
const tabletWidth = 912

const Bg = styled.div`
    display: flex;
    flex-direction: column;
    background: linear-gradient(180deg, #0c062d 0%, #4264e0 15.63%, #6ac3ff 60.06%),
        linear-gradient(180deg, #0c062d 0%, #6ac3ff 100%);
    width: 100vw;
    min-height: 100vh;
    overflow: hidden;
    background-attachment: local;
    @media (max-width: ${spWidth}px) {
        background: linear-gradient(180deg, #0c062d 0%, #4264e0 15.63%, #6ac3ff 60.06%),
            linear-gradient(180deg, #0c062d 0%, #6ac3ff 100%);
        background-size: 600px;
    }
`

const Sky = styled.header`
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    background: url(/images/bg-top-side.png) no-repeat center top;
    width: 26%;
    @media (max-width: ${spWidth}px) {
        display: none;
    }
`
const SkySp = styled.header`
    display: none;
    @media (max-width: ${spWidth}px) {
        position: relative;
        display: block;
        height: 140px;
        width: 100%;
        background: url(/images/bg-top-sp.png) no-repeat;
        background-position: center top;
        background-size: cover;
    }
`

const Main = styled.main`
    flex: 1;
`

const PageTitle = styled.div`
    display: flex;
    text-shadow: 0px 0px 30px #004a7c;
    position: absolute;
    top: 40px;
    left: 132px;
    font-size: 96px;
    transform: translateX(calc(-50% + 32px)) rotate(90deg);
    @media (max-width: 1155px) {
        font-size: 50px;
        top: 26px;
        left: 56px;
        transform: translateX(calc(-50% + 32px)) rotate(90deg);
    }
    @media (max-width: ${spWidth}px) {
        display: none;
    }
`
const PageTitleSp = styled.div`
    display: none;
    @media (max-width: ${spWidth}px) {
        display: inline-block;
        position: absolute;
        text-shadow: 0px 0px 30px #004a7c;
        top: 50%;
        left: 50%;
        transform: translateY(-50%) translateX(-50%);
        font-weight: 400;
        font-size: 36px;
    }
`

const Stars = styled.div`
    position: absolute;
    top: -36%;
    left: 15%;
    height: 250vh;
    margin: auto;
    transform: rotate(45deg);
    width: 100vw;
`

const Star = styled.div`
    position: absolute;
    width: 1px;
    height: 80%;
    top: 0;
    left: 50%;

    &::after {
        content: '';
        display: block;
        position: absolute;
        height: 100vh;
        width: 100%;
        top: -60%;
        left: 0;
        background-color: #ffffff;
        animation: drop 6s 0s infinite;
        animation-fill-mode: forwards;
    }

    :first-of-type {
        margin-left: -45%;
        &::after {
            animation-delay: 1.2s;
        }
    }
    :nth-of-type(2) {
        margin-left: 12px;
        &::after {
            zoom: 0.5;
            animation-delay: -0.2s;
        }
    }
    :nth-of-type(3) {
        margin-left: -28%;
        &::after {
            animation-delay: 4.5s;
        }
    }
    :nth-of-type(4) {
        margin-left: 18%;
        &::after {
            animation-delay: 3.3s;
        }
    }
    :last-of-type {
        margin-left: 35%;
        &::after {
            animation-delay: 2.5s;
        }
    }

    @keyframes drop {
        0% {
            top: -45%;
            height: 100vw;
        }
        80% {
            height: 0;
        }
        100% {
            height: 0;
            top: 100%;
        }
    }
`

const Footer = styled.footer`
    background: url(/images/bg-bottom-half.png) no-repeat bottom;
    bottom: 0;
    text-align: center;
    font-size: 0.92rem;
    font-weight: bold;
    color: #ffffff;
    letter-spacing: 0.1em;
    width: 100vw;
    padding-bottom: 88px;
    display: flex;
    align-items: end;
    justify-content: center;
    background-size: cover;
    background-repeat: no-repeat;
    @media (max-width: ${tabletWidth}px) {
        padding-bottom: 48px;
        font-size: 0.62rem;
    }
    @media (max-width: ${spWidth}px) {
        background: url(/images/bg-bottom-sp.png) no-repeat bottom;
        padding-bottom: 16px;
        font-size: 0.62rem;
    }
    @media (orientation: landscape) and (max-width: ${pcWidth}px) {
        font-size: 0.62rem;
    }
`

export const DefaultLayout: VFC<DefaultLayoutProps> = ({ children, pageTitle }) => {
    return (
        <motion.div animate={{ opacity: 1 }} initial={{ opacity: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.5 }}>
            <Bg>
                <Sky>
                    <PageTitle>
                        <p className="absolute text-c-secondary w-max h-20 font-RocknRoll tracking-widest">{pageTitle}</p>
                    </PageTitle>
                </Sky>
                <Main>
                    <CommonMeta />
                    <div className="relative w-screen h-[140vh] overflow-hidden pointer-events-none">
                        <Stars>
                            <Star />
                            <Star />
                            <Star />
                            <Star />
                            <Star />
                            <Star />
                        </Stars>
                    </div>
                    <motion.h1
                        animate={{ opacity: 1, y: 0 }}
                        initial={{ opacity: 0, y: -100 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 1 }}
                        className="-mt-[140vh] relative md:w-5/6 w-3/6 max-w-sm lg:h-[24vw] h-[34vw] mx-auto">
                        <Link href="/">
                            <Image src="/images/logo.svg" alt="惑星（ほし）のさみだれ" layout="fill" objectFit="contain" />
                        </Link>
                    </motion.h1>
                    <SkySp>
                        <PageTitleSp>
                            <p className="text-c-secondary w-max font-RocknRoll tracking-widest">{pageTitle}</p>
                        </PageTitleSp>
                    </SkySp>
                    <div className="w-full relative z-[1] mt-8">{children}</div>
                </Main>
                <Footer>
                    <small className="md:pt-[25%] pt-[160%]">©水上悟志・少年画報社／ある時代の指輪の騎士団</small>
                </Footer>
            </Bg>
        </motion.div>
    )
}
